import { useCallback, useEffect, useRef } from 'react';
import { useAtom } from 'jotai';
import { useMyInfo } from 'shared/src/queries';
import type { MarketRankingAgeOption } from 'shared/src/api';
import { marketRankingFilterAtom } from '../states/marketRankingFilter.atom';
import { useMarketRankingFilterOptions } from '../queries/useMarketRankingFilterOptions';

/**
 * 마켓 랭킹 진입 시 유저 `age_range`를 기반으로 연령 필터를 최초 1회 자동 선택.
 *
 * 매칭 규칙 (Android `RankingMarketFilterViewModel.findMatchingAgeFilter` 와 동일):
 * 1. `name` 정확 일치 (예: "20대 중반" === "20대 중반")
 * 2. 앞 숫자 추출 후, 유저 나이 이하인 필터 중 가장 큰 값
 *
 * 사용자가 명시적으로 선택한 값이 이미 있으면 덮어쓰지 않고,
 * 마켓 타입 전환으로 인한 리셋 이후에도 재적용하지 않음(세션 1회).
 */
export const useAutoSelectAgeFilter = (marketTypeSno: number) => {
  const [filter, setFilter] = useAtom(marketRankingFilterAtom);
  const { data: myInfo } = useMyInfo();
  const { data: filterOptions } = useMarketRankingFilterOptions(marketTypeSno);
  const hasAppliedRef = useRef(false);

  const findMatchingAgeFilter = useCallback(
    (
      userAgeRange: string,
      ages: MarketRankingAgeOption[],
    ): MarketRankingAgeOption | null => {
      const exact = ages.find(f => f.name === userAgeRange);
      if (exact != null) return exact;

      const userAge = Number(userAgeRange.match(/\d+/)?.[0]);
      if (Number.isNaN(userAge)) return null;

      const candidates = ages
        .map(f => {
          const age = Number(f.name.match(/\d+/)?.[0]);
          return Number.isNaN(age) ? null : { filter: f, age };
        })
        .filter(
          (v): v is { filter: MarketRankingAgeOption; age: number } =>
            v != null && v.age <= userAge,
        );

      if (candidates.length === 0) return null;
      return candidates.reduce((a, b) => (a.age >= b.age ? a : b)).filter;
    },
    [],
  );

  useEffect(() => {
    if (hasAppliedRef.current) return;
    if (filter.selectedAgeValues.length > 0) return;
    if (myInfo?.age_range == null) return;

    const matched = findMatchingAgeFilter(
      myInfo.age_range,
      filterOptions?.ages ?? [],
    );
    if (matched == null) return;

    setFilter(prev => ({ ...prev, selectedAgeValues: [matched.value] }));
    hasAppliedRef.current = true;
  }, [
    myInfo?.age_range,
    filterOptions,
    filter.selectedAgeValues,
    setFilter,
    findMatchingAgeFilter,
  ]);
};
